/**
 * Runtime-only Expired status helpers for Portal.
 *
 * Expiration is computed on the client from fields already returned by the existing
 * `POST /api/v3/devices` response (`credentialType`, `credentialEndValidity`) — no backend
 * change, no persisted status. A device qualifies as Expired only when it has ≥1 expiring
 * credential AND every expiring credential is expired. Missing expiration data is treated as
 * "unknown, not expired" — a safe fallback so partial data never silently flips a device to
 * Expired.
 */

/**
 * Credential type values that carry a meaningful expiration date.
 *
 * Naming intentionally covers both the UCMS DB algorithm names (`pkirsa2048`, `pkiecc256` —
 * the values stored in `workflow_certificate_xref.credential_type`) and the API-facing alias
 * (`x509` — what the Portal `/api/v3/devices` response actually puts on each credential's
 * `type` / `credentialType` field). Comparison is case-insensitive (see `isExpiringType`),
 * so DB `'X509'` and `'x509'` both match.
 */
export const EXPIRING_CREDENTIAL_TYPES: ReadonlySet<string> = new Set([
    'pkirsa2048',
    'pkiecc256',
    'x509',
]);

export type CredentialLike = {
    credentialType?: string | null;
    credentialEndValidity?: string | Date | null;
    status?: string | null;
    // Portal's Credential DTO nests the validity inside `data.endValidity` — keep a pointer
    // so render sites that already destructure `data` can pass the nested object through.
    data?: {
        endValidity?: string | Date | null;
    } | null;
    type?: string | null;
};

export type DeviceLike = {
    status?: string | null;
    credentials?: ReadonlyArray<CredentialLike> | null;
};

export const EXPIRED_STATUS = 'expired';

const endValidityOf = (c: CredentialLike): string | Date | null | undefined =>
    c.credentialEndValidity ?? c.data?.endValidity ?? null;

const credentialTypeOf = (c: CredentialLike): string | undefined =>
    (c.credentialType ?? c.type ?? undefined)?.toString();

const isExpiringType = (c: CredentialLike): boolean => {
    const t = credentialTypeOf(c);
    return !!t && EXPIRING_CREDENTIAL_TYPES.has(t.toLowerCase());
};

export const isCredExpired = (c: CredentialLike | null | undefined, now: number = Date.now()): boolean => {
    if (!c || !isExpiringType(c)) {
        return false;
    }
    const end = endValidityOf(c);
    if (!end) {
        return false;
    }
    const t = typeof end === 'string' ? Date.parse(end) : end.getTime();
    return Number.isFinite(t) && t < now;
};

export const isDeviceExpired = (d: DeviceLike | null | undefined, now: number = Date.now()): boolean => {
    if (!d || !d.credentials) {
        return false;
    }
    const expiring = d.credentials.filter(isExpiringType);
    if (expiring.length === 0) {
        return false;
    }
    if (!expiring.every((c) => !!endValidityOf(c))) {
        return false;
    }
    return expiring.every((c) => isCredExpired(c, now));
};

/**
 * Returns the status to render for a credential. When the credential is dynamically expired,
 * returns `"expired"`; otherwise returns the credential's stored status unchanged.
 */
export const displayedCredStatus = (
    c: CredentialLike | null | undefined,
    now: number = Date.now(),
): string => {
    if (isCredExpired(c, now)) {
        return EXPIRED_STATUS;
    }
    return (c?.status ?? '').toString();
};

/**
 * Returns the status to render for a device. When the device qualifies as Expired, replaces the
 * primary status with `"expired"`; otherwise returns the device's stored status unchanged.
 */
export const displayedDeviceStatus = (
    d: DeviceLike | null | undefined,
    now: number = Date.now(),
): string => {
    if (isDeviceExpired(d, now)) {
        return EXPIRED_STATUS;
    }
    return (d?.status ?? '').toString();
};

/**
 * Combined display helper — handles both device objects (with a `.credentials` array) and
 * single credential objects (with `credentialType` / `endValidity`). Returns `"expired"` when
 * either shape resolves as expired, otherwise the stored `status`.
 *
 * <p>Used by {@code DeviceStatus.jsx}, which is reused to render both device rows (main
 * Identities table) and individual credential rows (device-details expanded view). A single
 * helper keeps both call sites correct without the component needing to know the shape.
 */
export const displayedStatus = (
    obj: (DeviceLike & CredentialLike) | null | undefined,
    now: number = Date.now(),
): string => {
    if (isDeviceExpired(obj, now)) {
        return EXPIRED_STATUS;
    }
    if (isCredExpired(obj, now)) {
        return EXPIRED_STATUS;
    }
    return (obj?.status ?? '').toString();
};
